import { useTranslation } from "react-i18next";
import { ArchivedCategoryState, UnknownCategoryNote } from "../ObjectSelectionStep/CategoryNotice";
import { EmptyCategoryState, STATION_CATEGORY_NOTICE_KEYS, useShowAllLocations } from "./stationCategoryNotice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FilteredSelect from "./FilteredSelect";
import { RootState } from "../../../../../redux/store";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { useStationLocationList } from "./useStationLocationList";
import { bookingCreationCompleted } from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import routes from "../../../../../routes/routes";
import { LocationPros } from "../../../../../types/baseTypes";
import BookLocationHeader from "../../util/BookLocationHeader";

/** The booking flows that start by picking a station from `GET /location?services=…`. */
export const LOCATION_LIST_SERVICES = ["sup", "rental", "p2p", "parcel"] as const;
export type LocationListService = (typeof LOCATION_LIST_SERVICES)[number];
export const isLocationListService = (service: string): service is LocationListService =>
  (LOCATION_LIST_SERVICES as readonly string[]).includes(service);

interface UseCaseLocationSelectionStepProps {
  /** The `services` filter the flow's dialog loads its locations with. */
  service: LocationListService;
}

/**
 * The location step shared by the SUP, rental, P2P and parcel booking flows
 * (formerly four identical copies, one per flow).
 *
 * Four distinct answers, never one: still loading, failed to load, loaded but
 * empty, loaded with locations — the same split as
 * `TemporaryStorageLocationSelectionStep`. The copies tested the Redux mirror
 * `useCaseLocations` for `undefined`, which it never is (initialised to `[]`),
 * so an empty list rendered as a disabled select with no explanation: a
 * station closed for the winter looked like a broken app.
 *
 * The query shares its cache entry with the flow's `BookLocationDialog`, so
 * this is not a second request.
 */
export default function UseCaseLocationSelectionStep({ service }: Readonly<UseCaseLocationSelectionStepProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, isLoading, isError, categoryNotice, productCategory } = useStationLocationList(service);
  const tenantServices = useSelector(
    (state: RootState) => state.tenantConfig.config?.services
  );

  const locations: LocationPros[] = Array.isArray(data) ? data : [];
  const offersVouchers = Array.isArray(tenantServices) && tenantServices.includes("voucher");

  // Leave this flow cleanly (as its close button does) before entering the
  // voucher flow, which starts from the initial booking-creation state.
  const goToVoucherPurchase = () => {
    dispatch(bookingCreationCompleted());
    navigate(routes.voucherPath);
  };

  const showAllLocations = useShowAllLocations();

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex w-full items-center justify-center py-6" aria-live="polite">
          <Spinner size="md" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-6" role="alert">
          <p className="text-center font-semibold text-foreground">
            {t("book_location.error_loading_locations")}
          </p>
          <p className="text-center text-sm text-foreground/70">
            {t("common.please_try_again")}
          </p>
        </div>
      );
    }
    // #3429 step 3: entered from a category tile whose category was retired,
    // the empty list is explained — not shown as "closed for the season".
    if (categoryNotice?.kind === "archived") {
      return (
        <ArchivedCategoryState
          notice={categoryNotice}
          keys={STATION_CATEGORY_NOTICE_KEYS}
          onShowAll={showAllLocations}
        />
      );
    }
    // A live category that no station offers right now is not "closed for
    // the season" — the stations are open for everything else.
    if (locations.length === 0 && productCategory) {
      return <EmptyCategoryState onShowAll={showAllLocations} />;
    }
    if (locations.length === 0) {
      return (
        // A screen of its own: the flow page hides its step title ("Standort
        // auswählen") and "Weiter" around it (useNoBookableStation), so this
        // title takes the title's place.
        <div className="flex flex-col gap-3 pt-2 pb-6" aria-live="polite">
          <div className="mb-3">
            <BookLocationHeader text={t("book_location.no_location_available.title")} />
          </div>
          <p className="text-center text-foreground/70">
            {t(
              service === "sup"
                ? "book_location.no_location_available.body_sup"
                : "book_location.no_location_available.body"
            )}
          </p>
          {offersVouchers && (
            <>
              <p className="text-center text-foreground/70">
                {t("book_location.no_location_available.voucher_hint")}
              </p>
              <Button variant="primary" size="lg" className="mt-4 w-full" onClick={goToVoucherPurchase}>
                {t("book_location.no_location_available.voucher_cta")}
              </Button>
            </>
          )}
        </div>
      );
    }
    return (
      <>
        {categoryNotice?.kind === "unknown" && (
          <UnknownCategoryNote keys={STATION_CATEGORY_NOTICE_KEYS} />
        )}
        <FilteredSelect locations={locations} />
      </>
    );
  };

  return <div className="flex flex-col justify-center px-5">{renderBody()}</div>;
}
